import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from '@/app/i18n/TranslationContext';
import styles from './CountrySelect.module.scss';
import { i18nConfig } from '@/app/i18n/configs';
import {
  removeLocaleFromPath,
  removeDefaultLocaleFromPath,
} from '@/app/i18n/utils';

const getLocaleData: (t: (key: string) => string) => {
  [locale: string]: { name: string };
} = (t: (key: string) => string) => ({
  'en-us': { name: t('english') },
  ar: { name: t('arabic') },
  nl: { name: t('dutch') },
  fr: { name: t('french') },
  de: { name: t('german') },
  it: { name: t('italian') },
  es: { name: t('spanish') },
});

const CountrySelect = React.memo(({ locale }: { locale: string }) => {
  const { t } = useTranslation();
  const pathname = usePathname();
  const router = useRouter();
  const { locales } = i18nConfig;

  // The locale <select> is always as wide as its widest option,
  // but we only want it to be as wide as the currently selected option.
  // So we render a hidden <select> that only has one <option> (the currently
  // selected locale) and use that to set the width of the locale <select>
  const countrySelectRef = useRef<HTMLSelectElement>(null);
  const hiddenSelectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    const ro = new ResizeObserver(() => {
      if (countrySelectRef.current && hiddenSelectRef.current?.clientWidth) {
        countrySelectRef.current.style.setProperty(
          'width',
          `${hiddenSelectRef.current.clientWidth}px`
        );
      }
    });

    if (hiddenSelectRef.current) {
      ro.observe(hiddenSelectRef.current);
    }

    return () => {
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    const dir = locale == 'ar' ? 'rtl' : 'ltr';
    document.querySelector('html')?.setAttribute('dir', dir);
  }, [locale]);

  const onCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLocale = e.target.value;

    // Validate that the newLocale is in our allowed locales list
    if (!locales?.includes(newLocale)) {
      console.warn('Invalid locale selected:', newLocale);
      return;
    }

    // Only proceed if it's a different locale
    if (newLocale !== locale) {
      const basePath = pathname ? removeLocaleFromPath(pathname) : '';
      const newPath = basePath ? `/${newLocale}${basePath}` : `/${newLocale}`;
      const finalPath = removeDefaultLocaleFromPath(newPath);

      router.push(finalPath);
    }
  };

  const localeData = getLocaleData(t);

  return (
    <div className={styles.countrySelectContainer}>
      <div className={styles.countrySelectWrapper}>
        <div className={styles.countrySelectOverlay} />

        <label htmlFor="select-country" className={styles.globeAlt}>
          <span className={styles.visuallyHidden}>{t('Select country')}</span>
        </label>
        <select
          id="select-country"
          name="select-country"
          className={styles.countrySelect}
          ref={countrySelectRef}
          value={locale}
          aria-label="language select"
          onChange={onCountryChange}
          data-testid={'country-select'}
        >
          {locales?.map(value => (
            <option key={value} value={value}>
              {localeData[value]?.name}
            </option>
          ))}
        </select>

        {/* use a hidden <select> to find the correct width of the visible country <select> */}
        <label
          htmlFor="select-country-hidden"
          className={styles.visuallyHidden}
        >
          {t('Select country')}
        </label>
        <select
          id="select-country-hidden"
          name="select-country-hidden"
          className={`${styles.countrySelect} ${styles.hiddenSelect}`}
          ref={hiddenSelectRef}
        >
          <option value={locale}>{localeData[locale as string]?.name}</option>
        </select>
        <div
          className={styles.countrySelectPopup}
          data-testid={'country-select-popup'}
        >
          {locales?.map(value => {
            const basePath = pathname ? removeLocaleFromPath(pathname) : '';
            const newHref = basePath ? `/${value}${basePath}` : `/${value}`;
            return (
              <Link
                href={removeDefaultLocaleFromPath(newHref)}
                key={value}
                locale={value}
                className={styles.countrySelectorLink}
                lang={value}
              >
                <span>{localeData[value]?.name}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
});

CountrySelect.displayName = 'CountrySelect';

export default React.memo(CountrySelect);
